import { Box, Container, Attribute, Button } from "@/components/atoms";
import { Typography, Divider, Snackbar, Alert } from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  ErrorState,
  LoadingState,
  UnauthenticatedState,
} from "@/components/views";
import { useUserContext } from "@/providers/contexts";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useEffect, useState } from "react";
import { MembershipType, MembershipTypeNames, User } from "@/types/user";
import {
  useCreateStripeCheckoutSession,
  useUpdateUser,
  useUpdateUserCredential,
  useUpdateUserEducation,
  useUpdateUserHealthcareDomain,
} from "@/hooks";
import { ButtonSet, EditableField, LabeledList } from "@/components/molecules";
import { UserCredentialDropdown } from "./UserCredentialDropdown";
import { UserEducationDropdown } from "./UserEducationDropdown";
import { UserHealthcareDomainDropdown } from "./UserHealthcareDomainDropdown";
import {
  useUpdateCredentialManager,
  useUpdateEducationManager,
  useUpdateHealthcareDomainManager,
} from "@/hooks/users/handlers";

dayjs.extend(relativeTime);

export const Account = () => {
  /* --------------------- Hooks --------------------- */
  const navigate = useNavigate();

  const {
    user,
    loading: userLoading,
    error: userError,
    isAuthenticated,
    refetchUser,
  } = useUserContext();

  const {
    updateUser,
    loading: updateUserLoading,
    error: updateUserError,
    success: userUpdateSuccess,
  } = useUpdateUser();

  const {
    loading: updateUserCredentialLoading,
    error: updateUserCredentialError,
    updateUserCredential,
    success: credentialSuccess,
  } = useUpdateUserCredential();

  const {
    userCredentials,
    selectedCredentials,
    setSelectedCredentials,
    credentialDataObject,
    credentialsHaveChanged,
    resetCredentials,
  } = useUpdateCredentialManager(user);

  const {
    loading: updateUserDomainLoading,
    error: updateUserDomainError,
    updateUserHealthcareDomain,
    success: domainSuccess,
  } = useUpdateUserHealthcareDomain();

  const {
    userHealthcareDomains,
    selectedHealthcareDomains,
    setSelectedHealthcareDomains,
    healthcareDomainDataObject,
    healthcareDomainsHaveChanged,
    resetHealthcareDomains,
  } = useUpdateHealthcareDomainManager(user);

  const {
    userEducations,
    selectedEducations,
    setSelectedEducations,
    educationDataObject,
    educationsHaveChanged,
    resetEducations,
  } = useUpdateEducationManager(user);

  const {
    loading: updateUserEducationLoading,
    error: updateUserEducationError,
    updateUserEducation,
    success: educationSuccess,
  } = useUpdateUserEducation();

  const {
    createCheckoutSession,
    loading: stripeLoading,
    error: stripeError,
  } = useCreateStripeCheckoutSession();
  /* --------------------- States--------------------- */

  const [editing, setEditing] = useState(false);

  const [email, setEmail] = useState(user?.email || "");
  const [password, setPassword] = useState("");
  const [emailChanged, setEmailChanged] = useState(false);

  const [firstName, setFirstName] = useState<User["first_name"] | undefined>(
    user?.first_name || "",
  );
  const [lastName, setLastName] = useState<User["last_name"] | undefined>(
    user?.last_name || "",
  );

  const [snackBar, setSnackBar] = useState({
    state: false,
    msg: "",
    severity: "success" as "error" | "success",
  });

  /* --------------------- Constants--------------------- */
  const membershipType = user?.membership_type?.name;
  const isSSOUser = !!user?.uid;

  const noChanges =
    firstName === user?.first_name &&
    lastName === user?.last_name &&
    email === user?.email &&
    !credentialsHaveChanged() &&
    !educationsHaveChanged() &&
    !healthcareDomainsHaveChanged();

  const loading =
    updateUserLoading ||
    updateUserCredentialLoading ||
    updateUserDomainLoading ||
    updateUserEducationLoading ||
    stripeLoading;

  const error =
    updateUserError ||
    updateUserCredentialError ||
    updateUserDomainError ||
    updateUserEducationError ||
    stripeError;

  const hasSuccessfulUpdate =
    userUpdateSuccess || credentialSuccess || educationSuccess || domainSuccess;

  /* --------------------- Lifecycle Methods --------------------- */
  useEffect(() => {
    if (user) {
      setEmail(user.email || "");
      setFirstName(user.first_name || "");
      setLastName(user.last_name || "");
    }
  }, [user]);

  useEffect(() => {
    if (hasSuccessfulUpdate) {
      refetchUser();
      setSnackBar({
        state: true,
        msg: "Profile updated successfully",
        severity: "success",
      });
      setEditing(false);
    }
  }, [hasSuccessfulUpdate]);

  useEffect(() => {
    if (error) {
      setSnackBar({
        state: true,
        msg: error,
        severity: "error",
      });
    }
  }, [error]);

  useEffect(() => {
    if (snackBar.state) {
      const timer = setTimeout(() => {
        setSnackBar(prev => ({ ...prev, state: false }));
      }, 6000);
      return () => clearTimeout(timer);
    }
  }, [snackBar.state]);

  if (userError) return <ErrorState errors={userError} />;
  if (userLoading) return <LoadingState />;
  if (!isAuthenticated) return <UnauthenticatedState />;

  /* --------------------- Handles --------------------- */

  const handleCredentialUpdates = async () => {
    if (!user) return;

    const originalIds = user.user_credentials.map(c => c.credential.id);
    const newIds = credentialDataObject.map(cd => cd.credential_id);

    const removed = originalIds.filter(id => !newIds.includes(id));
    const addedOrUpdated = credentialDataObject;

    // Remove credentials
    for (const credentialId of removed) {
      await updateUserCredential({
        credential_id: credentialId,
        remove_credential: true,
      });
    }

    // Add or update credentials
    for (const data of addedOrUpdated) {
      await updateUserCredential(data);
    }
  };

  const handleEducationUpdates = async () => {
    if (!user) return;

    const originalIds = user.user_educations.map(e => e.education.id);
    const newIds = educationDataObject.map(ed => ed.education_id);

    const removed = originalIds.filter(id => !newIds.includes(id));
    const addedOrUpdated = educationDataObject;

    // Remove educations
    for (const educationId of removed) {
      await updateUserEducation({
        education_id: educationId,
        remove_education: true,
        institution_name: "",
      });
    }

    // Add or update educations
    for (const data of addedOrUpdated) {
      await updateUserEducation(data);
    }
  };

  const handleHealthcareDomainUpdates = async () => {
    if (!user) return;

    const originalIds = user.healthcare_domains.map(
      d => d.healthcare_domain.id,
    );
    const newIds = healthcareDomainDataObject.map(d => d.healthcare_domain_id);

    const removed = originalIds.filter(id => !newIds.includes(id));
    const addedOrUpdated = healthcareDomainDataObject;

    // Remove domains
    for (const domainId of removed) {
      await updateUserHealthcareDomain({
        healthcare_domain_id: domainId,
        remove_healthcare_domain: true,
      });
    }

    // Add or update domains
    for (const data of addedOrUpdated) {
      await updateUserHealthcareDomain(data);
    }
  };

  const handleSave = async () => {
    const payload = {
      first_name: firstName,
      last_name: lastName,
      ...(emailChanged && { email, password }),
    };

    updateUser(payload);

    await handleCredentialUpdates();
    await handleEducationUpdates();
    await handleHealthcareDomainUpdates();

    refetchUser();
  };

  const handleCancel = () => {
    if (!user) return;

    setEmail(user.email || "");
    setFirstName(user.first_name || "");
    setLastName(user.last_name || "");
    setPassword("");
    setEmailChanged(false);

    resetCredentials();
    resetEducations();
    resetHealthcareDomains();

    setEditing(false);
  };

  const handleMembershipChange = async (
    membershipTypeName: MembershipType["name"],
  ) => {
    if (!user || !user.membership_type) {
      setSnackBar({
        state: true,
        msg: "Please register or log in first.",
        severity: "error",
      });
      setTimeout(() => navigate("/register"), 2000);
      return;
    }
    localStorage.setItem("prevMembershipName", user.membership_type.name);

    try {
      await createCheckoutSession({ new_membership_name: membershipTypeName });
      setSnackBar({
        state: true,
        msg: `Redirecting to Stripe to change your membership to ${membershipTypeName}...`,
        severity: "success",
      });
    } catch (err) {
      setSnackBar({
        state: true,
        msg: "An error occurred while starting the checkout session.",
        severity: "error",
      });
    }
  };

  return (
    <Container
      componentName="account"
      sx={{ flexDirection: "column", justifyContent: "flex-start" }}
    >
      <Snackbar
        open={snackBar.state}
        autoHideDuration={6000}
        onClose={() => setSnackBar(prev => ({ ...prev, state: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackBar.severity}
          variant="filled"
          onClose={() => setSnackBar(prev => ({ ...prev, state: false }))}
        >
          {snackBar.msg}
        </Alert>
      </Snackbar>
      <Box
        componentName="account-parent"
        border={false}
        sx={{ display: "flex", flexDirection: "column", height: "100%", my: 2 }}
      >
        <Box
          componentName="profile-upper"
          border
          flexDirection="column"
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            flex: 1,
            padding: 2,
          }}
        >
          <Box
            componentName="left"
            sx={{ flex: 1, mb: { xs: 2, md: 0 }, mr: { md: 2 }, p: 2 }}
          >
            <Typography variant="h4" gutterBottom>
              Account Details
            </Typography>
            <Divider color="primary.main" sx={{ width: "80%", mb: 2 }} />

            {editing ? (
              <Box componentName="edit" border={false}>
                <EditableField
                  label="Email"
                  value={email}
                  onChange={e => {
                    setEmail(e.target.value);
                    setEmailChanged(e.target.value !== user?.email);
                  }}
                  disabled={isSSOUser}
                  helperText={
                    isSSOUser
                      ? "Email cannot be changed for SSO users"
                      : emailChanged
                        ? "You’ll need to enter your password to confirm"
                        : ""
                  }
                />

                {emailChanged && (
                  <EditableField
                    label="Confirm Password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    type="password"
                    required
                  />
                )}

                <EditableField
                  label="First Name"
                  value={firstName || ""}
                  onChange={e => setFirstName(e.target.value)}
                />

                <EditableField
                  label="Last Name"
                  value={lastName || ""}
                  onChange={e => setLastName(e.target.value)}
                />

                <UserCredentialDropdown
                  userCredentials={userCredentials}
                  selectedCredentials={selectedCredentials || []}
                  setSelectedCredentials={setSelectedCredentials}
                />
                <UserEducationDropdown
                  userEducations={userEducations}
                  selectedEducations={selectedEducations}
                  setSelectedEducations={setSelectedEducations}
                />
                <UserHealthcareDomainDropdown
                  userDomains={userHealthcareDomains}
                  selectedHealthcareDomains={selectedHealthcareDomains}
                  setSelectedHealthcareDomains={setSelectedHealthcareDomains}
                />

                <ButtonSet
                  componentName="account-details"
                  leftBoxHandler={handleCancel}
                  rightBoxHandler={handleSave}
                  leftBoxText="Cancel"
                  rightBoxText={loading ? "Saving..." : "Save"}
                  disableRightButton={noChanges || loading}
                  sx={{
                    mt: 4,
                  }}
                />

                {error && (
                  <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                    {error}
                  </Typography>
                )}
              </Box>
            ) : (
              <>
                <Attribute
                  componentName="user-email"
                  label="Email"
                  value={user?.email || "Not set"}
                />

                <Attribute
                  componentName="user-first-name"
                  label="First Name"
                  value={user?.first_name || "Not set"}
                />

                <Attribute
                  componentName="user-last-name"
                  label="Last Name"
                  value={user?.last_name || "Not set"}
                />

                <LabeledList
                  label="Credentials"
                  items={user?.user_credentials.map(
                    c => c.credential.credential_name,
                  )}
                />

                <LabeledList
                  label="Education"
                  items={user?.user_educations.map(
                    e => e.education.education_name,
                  )}
                />

                <LabeledList
                  label="Healthcare Domains"
                  items={user?.healthcare_domains.map(
                    d => d.healthcare_domain.value,
                  )}
                />

                <Button
                  componentName="edit"
                  variant="contained"
                  onClick={() => setEditing(true)}
                  sx={{ mt: 2 }}
                >
                  Edit
                </Button>

                <Button
                  componentName="change-password"
                  variant="outlined"
                  onClick={() => navigate("/change-password")}
                  sx={{ mt: 2, ml: 2 }}
                >
                  Change Password
                </Button>
              </>
            )}
          </Box>

          <Box
            componentName="right"
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              alignItems: "flex-start",
              padding: 2,
            }}
          >
            <Typography variant="h6" gutterBottom>
              Upgrade Your Membership
            </Typography>
            <Box componentName="current-membership">
              <Attribute
                componentName="user-membership"
                label="Current Membership"
                value={membershipType || "Not set"}
              />
            </Box>

            {membershipType === "basic" && (
              <Box
                componentName="membership-basic"
                border
                justifyContent="space-between"
                display="flex"
                flexDirection="column"
                sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: "#f9f9f9",
                  width: "100%",
                  flex: 1,
                }}
              >
                <Typography variant="body1" component="div">
                  <p>
                    You're currently on the <strong>Basic</strong> plan.
                  </p>
                  <p>
                    If you'd like access to all filters, consider upgrading to
                    the <strong>Pro</strong> plan.
                  </p>
                  <p>
                    To unlock all features, including premium tools and
                    capabilities, consider upgrading to the{" "}
                    <strong>Premium</strong> plan.
                  </p>
                </Typography>
                <Box
                  componentName="premium-membership-actions"
                  border={false}
                  flexDirection="row"
                  alignItems="flex-start"
                  justifyContent="flex-end"
                  columnGap={2}
                  display="flex"
                  sx={{ mt: "auto" }}
                >
                  <Button
                    componentName="upgarde-to-pro"
                    variant="outlined"
                    color="primary"
                    onClick={() =>
                      handleMembershipChange(MembershipTypeNames.pro)
                    }
                  >
                    Upgrade to Pro
                  </Button>
                  <Button
                    componentName="upgrade-to-premium"
                    variant="outlined"
                    color="primary"
                    onClick={() =>
                      handleMembershipChange(MembershipTypeNames.premium)
                    }
                  >
                    Upgrade to Premium
                  </Button>
                </Box>
              </Box>
            )}

            {membershipType === "pro" && (
              <Box
                componentName="membership-pro"
                border
                justifyContent="space-between"
                display="flex"
                flexDirection="column"
                sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: "secondary.lightest",
                  width: "100%",
                  flex: 0.5,
                }}
              >
                <Typography variant="body1" component="div">
                  <p>
                    You're currently on the <strong>Pro</strong> plan.
                  </p>
                  <p>
                    Thank you for supporting PharmDs in IT. Your contribution
                    helps keep the platform running, fund new features, and
                    improve the experience for clinicians like you.
                  </p>
                  <p>
                    To unlock all features, including premium tools and
                    capabilities, consider upgrading to the{" "}
                    <strong>Premium</strong> plan.
                  </p>
                </Typography>

                <Box
                  componentName="pro-membership-actions"
                  border={false}
                  flexDirection="row"
                  alignItems="flex-start"
                  justifyContent="flex-end"
                  columnGap={2}
                  display="flex"
                  sx={{ mt: "auto" }}
                >
                  <Button
                    componentName="cancel-membership"
                    variant="outlined"
                    color="error"
                    onClick={() =>
                      handleMembershipChange(MembershipTypeNames.basic)
                    }
                  >
                    Cancel Membership (Downgrade to Basic)
                  </Button>
                  <Button
                    componentName="upgrade-to-premium"
                    variant="outlined"
                    color="primary"
                    onClick={() =>
                      handleMembershipChange(MembershipTypeNames.premium)
                    }
                  >
                    Upgrade to Premium
                  </Button>
                </Box>
              </Box>
            )}

            {membershipType === "premium" && (
              <Box
                componentName="membership-premium"
                border
                justifyContent="space-between"
                display="flex"
                flexDirection="column"
                sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: "primary.lightest",
                  width: "100%",
                  flex: 1,
                }}
              >
                <Typography variant="body1" component="div">
                  <p>
                    You're currently on the <strong>Premium</strong> plan.
                  </p>
                  <p>
                    Thank you for supporting PharmDs in IT. Your contribution
                    helps keep the platform running, fund new features, and
                    improve the experience for clinicians like you.
                  </p>
                  <p>
                    I truly hope this product helps make your job search easier
                    and more rewarding.
                  </p>
                </Typography>
                <Box
                  componentName="premium-membership-actions"
                  border={false}
                  flexDirection="row"
                  alignItems="flex-start"
                  justifyContent="flex-end"
                  columnGap={2}
                  display="flex"
                  sx={{ mt: "auto" }}
                >
                  <Button
                    componentName="cancel-membership"
                    variant="outlined"
                    color="error"
                    onClick={() =>
                      handleMembershipChange(MembershipTypeNames.basic)
                    }
                  >
                    Cancel Membership (Downgrade to Basic)
                  </Button>
                  <Button
                    componentName="downgrade-to-pro"
                    variant="outlined"
                    color="primary"
                    onClick={() =>
                      handleMembershipChange(MembershipTypeNames.pro)
                    }
                  >
                    Downgrade to Pro
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Container>
  );
};
